import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Search, X, Eye, Plus, Pencil, Trash2, Shield, AlertCircle, Ban } from 'lucide-react';
import { NairaInput } from '../../lib/nairaInput';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

const statusColors = { pending: 'bg-amber-100 text-amber-800', active: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', defaulted: 'bg-red-100 text-red-800', cancelled: 'bg-neutral-200 text-neutral-700' };
const statusLabels = { pending: 'Pending', active: 'Active', completed: 'Completed', defaulted: 'Defaulted', cancelled: 'Cancelled' };
const planLabels = { full: 'Full Payment', '1_months': '1 Month', '2_months': '2 Months', '3_months': '3 Months', '6_months': '6 Months (with 20% penalty)', '12_months': '12 Months (legacy)', '24_months': '24 Months (legacy)' };

const AllocationBadge = ({ type }) => {
  if (!type) return <span className="text-xs text-neutral-400">--</span>;
  const isFirm = type === 'firm';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isFirm ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
      title={isFirm ? 'Firm: fully paid — non-revocable' : 'Provisional: on installments — can be reassigned'}
      data-testid={`allocation-badge-${type}`}
    >
      {isFirm ? <Shield className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
      {isFirm ? 'Firm' : 'Provisional'}
    </span>
  );
};

const formatNaira = (amount) => {
  if (!amount && amount !== 0) return '--';
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
};

const emptyClient = {
  full_name: '', phone: '', email: '', alt_phone: '', address: '', occupation: '', date_of_birth: '',
  next_of_kin_name: '', next_of_kin_relationship: '', next_of_kin_phone: '', notes: '',
};

const emptyForm = {
  // Client
  client_id: '',
  client: { ...emptyClient },
  // Plot cascading
  estate_name: '',
  block_name: '',
  selected_plot_ids: [],
  // Sale info
  sale_date: new Date().toISOString().split('T')[0],
  total_amount: '',
  discount: '',
  discount_reason: '',
  initial_deposit: '',
  payment_plan: '3_months',
  commission: '',
  sales_person_marketer_id: '',
  referred_by_name: '',
};

const BOSales = () => {
  const navigate = useNavigate();
  const { role } = useBOAuth();
  const [sales, setSales] = useState([]);
  const [estates, setEstates] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [blockPlots, setBlockPlots] = useState([]);
  const [clients, setClients] = useState([]);
  const [realtors, setRealtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editSale, setEditSale] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [searchClient, setSearchClient] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirm, setConfirm] = useState({ open: false, sale: null });
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, sale: null });
  const formRef = useRef(null);

  // Scroll the open form into view when it appears (fixes "I clicked Edit but the form is way up there" jump).
  useEffect(() => {
    if (showForm && formRef.current) {
      // Use a tiny timeout so the form has rendered before we measure.
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [showForm]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [salesRes, estatesRes, clientsRes, realtorsRes] = await Promise.all([
        boAPI.getSales(),
        boAPI.getEstates(),
        boAPI.getClients(),
        boAPI.getRealtorNames(),
      ]);
      setSales(salesRes.data.sales || []);
      setEstates(estatesRes.data.estates || []);
      setClients(clientsRes.data.clients || []);
      setRealtors(realtorsRes.data.realtors || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await boAPI.getSales(params);
      setSales(res.data.sales || []);
    } catch (e) { toast.error('Failed to refresh sales'); }
  };

  useEffect(() => { if (!loading) fetchSales(); }, [statusFilter]);

  // Estate change → reload blocks
  useEffect(() => {
    if (!form.estate_name) { setBlocks([]); setBlockPlots([]); return; }
    boAPI.getEstateBlocks(form.estate_name).then(res => {
      setBlocks(res.data.blocks || []);
    }).catch(() => setBlocks([]));
  }, [form.estate_name]);

  // Block change → reload available plots
  useEffect(() => {
    if (!form.estate_name || !form.block_name) { setBlockPlots([]); return; }
    boAPI.getBlockPlots(form.estate_name, form.block_name, { available_only: !editSale }).then(res => {
      setBlockPlots(res.data.plots || []);
    }).catch(() => setBlockPlots([]));
  }, [form.estate_name, form.block_name, editSale]);

  // When user selects plots OR changes discount, auto-calc total_amount (only for new sales)
  useEffect(() => {
    if (editSale) return;
    if (!form.selected_plot_ids.length) return;
    const plotsTotal = form.selected_plot_ids.reduce((sum, pid) => {
      const p = blockPlots.find(bp => bp.id === pid);
      return sum + (p?.price || 0);
    }, 0);
    const discount = parseFloat(form.discount) || 0;
    const finalTotal = Math.max(0, plotsTotal - discount);
    if (plotsTotal > 0) {
      setForm(f => ({ ...f, total_amount: String(finalTotal) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.selected_plot_ids, form.discount]);

  const togglePlot = (plotId) => {
    setForm(f => {
      const exists = f.selected_plot_ids.includes(plotId);
      return { ...f, selected_plot_ids: exists ? f.selected_plot_ids.filter(id => id !== plotId) : [...f.selected_plot_ids, plotId] };
    });
  };

  const filteredClients = useMemo(() => {
    const q = searchClient.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients.filter(c => (c.full_name || '').toLowerCase().includes(q) || (c.phone || '').includes(q)).slice(0, 8);
  }, [searchClient, clients]);

  const pickExistingClient = (c) => {
    setForm(f => ({
      ...f,
      client_id: c.id,
      client: {
        full_name: c.full_name || '',
        phone: c.phone || '',
        email: c.email || '',
        alt_phone: c.alt_phone || '',
        address: c.address || '',
        occupation: c.occupation || '',
        date_of_birth: c.date_of_birth || '',
        next_of_kin_name: c.next_of_kin_name || '',
        next_of_kin_relationship: c.next_of_kin_relationship || '',
        next_of_kin_phone: c.next_of_kin_phone || '',
        notes: '',
      },
    }));
    setShowClientPicker(false);
    setSearchClient('');
  };

  const clearClientLink = () => {
    setForm(f => ({ ...f, client_id: '', client: { ...emptyClient } }));
  };

  const openCreateForm = () => {
    setEditSale(null);
    setForm({ ...emptyForm, client: { ...emptyClient }, selected_plot_ids: [] });
    setShowForm(true);
  };

  const openEditForm = (s) => {
    setEditSale(s);
    setForm({
      client_id: s.client_id || '',
      client: { ...emptyClient,
        full_name: s.client_info?.full_name || '',
        phone: s.client_info?.phone || '',
        email: s.client_info?.email || '',
        address: s.client_info?.address || '',
      },
      estate_name: s.plot_info?.estate_name || '',
      block_name: s.plot_info?.location_block || '',
      selected_plot_ids: s.plot_ids || (s.plot_id ? [s.plot_id] : []),
      sale_date: s.sale_date || new Date().toISOString().split('T')[0],
      total_amount: String(s.total_amount || ''),
      initial_deposit: String(s.initial_deposit || ''),
      payment_plan: s.payment_plan || '12_months',
      commission: s.commission ? String(s.commission) : '',
      sales_person_marketer_id: s.sales_person_marketer_id || '',
      referred_by_name: s.referred_by_name || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editSale) {
      // Validations for new sale
      if (!form.client.full_name.trim() || !form.client.phone.trim()) {
        toast.error('Client full name and phone are required');
        return;
      }
      if (!form.selected_plot_ids.length) {
        toast.error('Select at least one plot');
        return;
      }
    }
    if (!form.total_amount || !form.initial_deposit) {
      toast.error('Total amount and initial deposit are required');
      return;
    }

    setSubmitting(true);
    try {
      if (editSale) {
        // Edit: only update sale-level fields (not plots / client switch).
        const payload = {
          total_amount: parseFloat(form.total_amount),
          payment_plan: form.payment_plan,
          sale_date: form.sale_date,
          commission: form.commission ? parseFloat(form.commission) : null,
        };
        await boAPI.updateSale(editSale.id, payload);
        toast.success('Sale updated');
      } else {
        const payload = {
          plot_ids: form.selected_plot_ids,
          sale_date: form.sale_date,
          total_amount: parseFloat(form.total_amount),
          initial_deposit: parseFloat(form.initial_deposit),
          payment_plan: form.payment_plan,
          commission: form.commission ? parseFloat(form.commission) : null,
          discount: form.discount ? parseFloat(form.discount) : 0,
          discount_reason: form.discount_reason || null,
          sales_person_marketer_id: form.sales_person_marketer_id || null,
          referred_by_name: form.referred_by_name || null,
        };
        if (form.client_id) {
          payload.client_id = form.client_id;
        } else {
          // Always send the client object so backend can match-by-phone or create
          const c = form.client;
          payload.client = {
            full_name: c.full_name.trim(),
            phone: c.phone.trim(),
            email: c.email || null,
            alt_phone: c.alt_phone || null,
            address: c.address || null,
            occupation: c.occupation || null,
            date_of_birth: c.date_of_birth || null,
            next_of_kin_name: c.next_of_kin_name || null,
            next_of_kin_relationship: c.next_of_kin_relationship || null,
            next_of_kin_phone: c.next_of_kin_phone || null,
            notes: c.notes || null,
          };
        }
        await boAPI.createSale(payload);
        toast.success('Sale recorded successfully');
      }
      setShowForm(false);
      setEditSale(null);
      setForm({ ...emptyForm });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save sale');
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = (s) => setConfirm({ open: true, sale: s });
  const cancelDelete = () => setConfirm({ open: false, sale: null });
  const confirmDelete = async () => {
    if (!confirm.sale) return;
    try {
      await boAPI.deleteSale(confirm.sale.id);
      toast.success('Sale deleted');
      cancelDelete();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete sale');
    }
  };

  const askCancel = (s) => setCancelConfirm({ open: true, sale: s });
  const closeCancelConfirm = () => setCancelConfirm({ open: false, sale: null });
  const confirmCancelSale = async () => {
    if (!cancelConfirm.sale) return;
    try {
      await boAPI.cancelSale(cancelConfirm.sale.id);
      toast.success('Sale cancelled — plot freed for reassignment. Payments preserved.');
      closeCancelConfirm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel sale');
    }
  };

  const isReadOnly = role === 'accountant';
  const totalRevenue = sales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
  const totalCollected = sales.reduce((sum, s) => sum + (s.total_paid || 0), 0);

  if (loading) {
    return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;
  }

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-sales-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Sales</h1>
            <p className="text-neutral-500 mt-1">{sales.length} sale{sales.length !== 1 ? 's' : ''} recorded</p>
          </div>
          {!isReadOnly && (
            <Button onClick={openCreateForm} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="record-sale-btn">
              <Plus className="h-4 w-4 mr-2" /> Record Sale
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Total Sales Value</p><p className="text-2xl font-bold text-neutral-900">{formatNaira(totalRevenue)}</p></CardContent></Card>
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Total Collected</p><p className="text-2xl font-bold text-green-700">{formatNaira(totalCollected)}</p></CardContent></Card>
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Outstanding</p><p className="text-2xl font-bold text-red-600">{formatNaira(totalRevenue - totalCollected)}</p></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <form onSubmit={(e) => { e.preventDefault(); fetchSales(); }} className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input placeholder="Search by client or plot..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-xl" data-testid="sales-search" />
          </form>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sales-status-filter">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="defaulted">Defaulted</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Record / Edit Sale Form */}
        {showForm && (
          <Card ref={formRef} className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{editSale ? 'Edit Sale' : 'Record New Sale'}</CardTitle>
                <CardDescription>{editSale ? 'Update payment plan, sale date or commission. Plots & client cannot be reassigned here.' : 'Capture client details, choose estate → block → plot(s), then payment terms'}</CardDescription>
              </div>
              <button onClick={() => { setShowForm(false); setEditSale(null); }} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">

                {/* CLIENT SECTION (new sale only) */}
                {!editSale && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-neutral-800">Client Details</h3>
                      <div className="flex items-center gap-2">
                        {form.client_id ? (
                          <Button type="button" variant="outline" size="sm" onClick={clearClientLink} className="rounded-xl text-xs" data-testid="sale-form-clear-client">
                            <X className="h-3 w-3 mr-1" /> Unlink (will create new)
                          </Button>
                        ) : (
                          <Button type="button" variant="outline" size="sm" onClick={() => setShowClientPicker(v => !v)} className="rounded-xl text-xs" data-testid="sale-form-pick-client">
                            <Search className="h-3 w-3 mr-1" /> Pick existing client
                          </Button>
                        )}
                      </div>
                    </div>

                    {showClientPicker && !form.client_id && (
                      <div className="rounded-xl border border-neutral-200 p-3 bg-neutral-50">
                        <Input
                          placeholder="Search by name or phone..."
                          value={searchClient}
                          onChange={(e) => setSearchClient(e.target.value)}
                          className="rounded-xl bg-white"
                          data-testid="sale-form-client-search"
                        />
                        <div className="mt-2 max-h-44 overflow-y-auto divide-y divide-neutral-200 bg-white rounded-xl border border-neutral-200">
                          {filteredClients.length === 0 && (
                            <p className="text-xs text-neutral-500 p-3">No matches. Continue typing to add as a new client.</p>
                          )}
                          {filteredClients.map(c => (
                            <button key={c.id} type="button" onClick={() => pickExistingClient(c)} className="w-full text-left px-3 py-2 hover:bg-primary-50" data-testid={`sale-client-pick-${c.id}`}>
                              <div className="font-medium text-sm">{c.full_name}</div>
                              <div className="text-xs text-neutral-500">{c.phone}{c.email ? ` · ${c.email}` : ''}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {form.client_id && (
                      <div className="rounded-xl border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
                        Linked to existing client: <strong>{form.client.full_name}</strong> ({form.client.phone})
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div><Label>Full Name *</Label><Input value={form.client.full_name} onChange={(e) => setForm({ ...form, client: { ...form.client, full_name: e.target.value } })} className="mt-1.5 rounded-xl" data-testid="sale-form-client-name" /></div>
                      <div><Label>Phone *</Label><Input value={form.client.phone} onChange={(e) => setForm({ ...form, client: { ...form.client, phone: e.target.value } })} className="mt-1.5 rounded-xl" data-testid="sale-form-client-phone" /></div>
                      <div><Label>Email</Label><Input type="email" value={form.client.email} onChange={(e) => setForm({ ...form, client: { ...form.client, email: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                      <div><Label>Alt Phone</Label><Input value={form.client.alt_phone} onChange={(e) => setForm({ ...form, client: { ...form.client, alt_phone: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                      <div><Label>Occupation</Label><Input value={form.client.occupation} onChange={(e) => setForm({ ...form, client: { ...form.client, occupation: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                      <div><Label>Date of Birth</Label><Input type="date" value={form.client.date_of_birth} onChange={(e) => setForm({ ...form, client: { ...form.client, date_of_birth: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                      <div className="sm:col-span-2 lg:col-span-3"><Label>Address</Label><Input value={form.client.address} onChange={(e) => setForm({ ...form, client: { ...form.client, address: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                      <div><Label>Next of Kin Name</Label><Input value={form.client.next_of_kin_name} onChange={(e) => setForm({ ...form, client: { ...form.client, next_of_kin_name: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                      <div><Label>Relationship</Label><Input value={form.client.next_of_kin_relationship} onChange={(e) => setForm({ ...form, client: { ...form.client, next_of_kin_relationship: e.target.value } })} placeholder="e.g., Spouse" className="mt-1.5 rounded-xl" /></div>
                      <div><Label>Next of Kin Phone</Label><Input value={form.client.next_of_kin_phone} onChange={(e) => setForm({ ...form, client: { ...form.client, next_of_kin_phone: e.target.value } })} className="mt-1.5 rounded-xl" /></div>
                    </div>
                  </div>
                )}

                {/* PROPERTY SECTION (new sale only) */}
                {!editSale && (
                  <div className="space-y-3 border-t pt-5">
                    <h3 className="font-semibold text-neutral-800">Property Selection</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label>Estate *</Label>
                        <select value={form.estate_name} onChange={(e) => setForm({ ...form, estate_name: e.target.value, block_name: '', selected_plot_ids: [] })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sale-form-estate">
                          <option value="">-- Select Estate --</option>
                          {estates.map(e => <option key={e.id} value={e.name}>{e.name} ({e.available_plots} avail)</option>)}
                        </select>
                      </div>
                      <div>
                        <Label>Block *</Label>
                        <select value={form.block_name} onChange={(e) => setForm({ ...form, block_name: e.target.value, selected_plot_ids: [] })} disabled={!form.estate_name} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm disabled:bg-neutral-100" data-testid="sale-form-block">
                          <option value="">-- Select Block --</option>
                          {blocks.map(b => <option key={b.block_name} value={b.block_name}>{b.block_name} ({b.available_count} avail / {b.total_count})</option>)}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <p className="text-xs text-neutral-500 mb-2">Selected: <strong>{form.selected_plot_ids.length}</strong> plot{form.selected_plot_ids.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>

                    {form.block_name && (
                      <div className="rounded-xl border border-neutral-200 p-3 bg-neutral-50">
                        <p className="text-xs text-neutral-500 mb-2">Tap one or more plots to include them in this sale</p>
                        <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                          {blockPlots.length === 0 && <p className="text-sm text-neutral-500 italic p-2">No available plots in this block</p>}
                          {blockPlots.map(p => {
                            const selected = form.selected_plot_ids.includes(p.id);
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => togglePlot(p.id)}
                                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${selected ? 'bg-primary-900 text-white border-primary-900' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-primary-50 hover:border-primary-300'}`}
                                data-testid={`sale-form-plot-${p.id}`}
                              >
                                {p.plot_number} <span className="opacity-70">· {formatNaira(p.price)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* PAYMENT SECTION */}
                <div className="space-y-3 border-t pt-5">
                  <h3 className="font-semibold text-neutral-800">Payment Terms</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div><Label>Sale Date *</Label><Input type="date" value={form.sale_date} onChange={(e) => setForm({ ...form, sale_date: e.target.value })} className="mt-1.5 rounded-xl" data-testid="sale-form-date" /></div>
                    {!editSale && (
                      <>
                        <div><Label>Discount (Naira)</Label><NairaInput value={form.discount} onChange={(v) => setForm({ ...form, discount: v })} className="mt-1.5 rounded-xl" data-testid="sale-form-discount" /></div>
                        <div><Label>Discount Reason</Label><Input value={form.discount_reason} onChange={(e) => setForm({ ...form, discount_reason: e.target.value })} placeholder="e.g., Loyalty, early payment" className="mt-1.5 rounded-xl" data-testid="sale-form-discount-reason" /></div>
                      </>
                    )}
                    <div><Label>Total Amount (Naira) *</Label><NairaInput value={form.total_amount} onChange={(v) => setForm({ ...form, total_amount: v })} className="mt-1.5 rounded-xl" data-testid="sale-form-amount" /></div>
                    <div><Label>Initial Deposit (Naira) *</Label><NairaInput value={form.initial_deposit} onChange={(v) => setForm({ ...form, initial_deposit: v })} className="mt-1.5 rounded-xl" data-testid="sale-form-deposit" disabled={!!editSale} /></div>
                    <div>
                      <Label>Payment Plan *</Label>
                      <select value={form.payment_plan} onChange={(e) => setForm({ ...form, payment_plan: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sale-form-plan">
                        <option value="full">Full Payment</option>
                        <option value="1_months">1 Month</option>
                        <option value="2_months">2 Months</option>
                        <option value="3_months">3 Months</option>
                        <option value="6_months">6 Months (extension — adds 20% penalty)</option>
                      </select>
                    </div>
                    <div><Label>Commission (Naira)</Label><NairaInput value={form.commission} onChange={(v) => setForm({ ...form, commission: v })} className="mt-1.5 rounded-xl" /></div>
                    {!editSale && (
                      <div>
                        <Label>Realtor</Label>
                        <select value={form.sales_person_marketer_id || (form.referred_by_name ? 'custom' : '')} onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'custom') setForm({ ...form, sales_person_marketer_id: '', referred_by_name: form.referred_by_name || '' });
                          else setForm({ ...form, sales_person_marketer_id: v, referred_by_name: '' });
                        }} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="sale-form-realtor">
                          <option value="">-- No Realtor --</option>
                          {realtors.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                          <option value="custom">Other (type name)</option>
                        </select>
                        {!form.sales_person_marketer_id && form.referred_by_name !== undefined && (form.referred_by_name || form.sales_person_marketer_id === '' ? false : true)}
                      </div>
                    )}
                    {!editSale && !form.sales_person_marketer_id && (
                      <div><Label>Other realtor name</Label><Input value={form.referred_by_name} onChange={(e) => setForm({ ...form, referred_by_name: e.target.value })} placeholder="optional" className="mt-1.5 rounded-xl" data-testid="sale-form-realtor-name" /></div>
                    )}
                  </div>

                  {form.total_amount && form.initial_deposit && form.payment_plan !== 'full' && (() => {
                    const months = { '1_months': 1, '2_months': 2, '3_months': 3, '6_months': 6, '12_months': 12, '24_months': 24 }[form.payment_plan] || 1;
                    const monthly = Math.round((parseFloat(form.total_amount) - parseFloat(form.initial_deposit)) / months);
                    return (
                      <div className="p-3 bg-blue-50 rounded-xl">
                        <p className="text-sm text-blue-800">
                          Monthly installment: <strong>{formatNaira(monthly)}</strong> for {planLabels[form.payment_plan]}
                        </p>
                      </div>
                    );
                  })()}
                </div>

                <div className="flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditSale(null); }} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="sale-form-submit">{submitting ? 'Saving...' : (editSale ? 'Save Changes' : 'Record Sale')}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Sales Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto -mx-0">
            <table className="w-full text-xs sm:text-sm min-w-[560px]" data-testid="sales-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-3 sm:px-4 py-3 font-medium text-neutral-600">Date</th>
                  <th className="text-left px-3 sm:px-4 py-3 font-medium text-neutral-600">Client</th>
                  <th className="text-left px-3 sm:px-4 py-3 font-medium text-neutral-600 hidden lg:table-cell">Plots</th>
                  <th className="text-right px-3 sm:px-4 py-3 font-medium text-neutral-600">Total</th>
                  <th className="text-right px-3 sm:px-4 py-3 font-medium text-neutral-600 hidden sm:table-cell">Paid</th>
                  <th className="text-right px-3 sm:px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Balance</th>
                  <th className="text-left px-3 sm:px-4 py-3 font-medium text-neutral-600 hidden sm:table-cell">Status</th>
                  <th className="text-left px-3 sm:px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Allocation</th>
                  <th className="text-right px-2 py-3 font-medium text-neutral-600 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sales.map(s => {
                  const plotsLabel = s.plots_info && s.plots_info.length
                    ? s.plots_info.map(p => p.plot_number).join(', ') + ` · ${s.plots_info[0].estate_name}`
                    : (s.plot_info ? `${s.plot_info.plot_number} · ${s.plot_info.estate_name}` : '--');
                  const isCancelled = s.payment_status === 'cancelled';
                  const isProvisional = s.allocation_type === 'provisional' && !isCancelled;
                  return (
                    <tr key={s.id} className={`border-b transition-colors ${isCancelled ? 'bg-neutral-50 opacity-60' : 'hover:bg-neutral-50'}`} data-testid={`sale-row-${s.id}`}>
                      <td className="px-3 sm:px-4 py-3 text-neutral-600 whitespace-nowrap">{s.sale_date}</td>
                      <td className="px-3 sm:px-4 py-3 font-medium max-w-[140px] sm:max-w-none truncate">{s.client_info?.full_name || '--'}</td>
                      <td className="px-3 sm:px-4 py-3 hidden lg:table-cell text-neutral-600 max-w-[260px] truncate" title={plotsLabel}>{plotsLabel}{s.plot_count > 1 ? ` (${s.plot_count} plots)` : ''}</td>
                      <td className="px-3 sm:px-4 py-3 text-right font-medium whitespace-nowrap">{formatNaira(s.total_amount)}</td>
                      <td className="px-3 sm:px-4 py-3 text-right text-green-700 hidden sm:table-cell whitespace-nowrap">{formatNaira(s.total_paid)}</td>
                      <td className="px-3 sm:px-4 py-3 text-right text-red-600 font-medium hidden md:table-cell whitespace-nowrap">{formatNaira(s.balance)}</td>
                      <td className="px-3 sm:px-4 py-3 hidden sm:table-cell"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[s.payment_status] || 'bg-neutral-100'}`}>{statusLabels[s.payment_status] || s.payment_status}</span></td>
                      <td className="px-3 sm:px-4 py-3 hidden md:table-cell"><AllocationBadge type={isCancelled ? null : s.allocation_type} /></td>
                      <td className="px-2 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => navigate(`/backoffice/sales/${s.id}`)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-600" title="View" data-testid={`view-sale-${s.id}`}><Eye className="h-4 w-4" /></button>
                          {!isReadOnly && !isCancelled && <button onClick={() => openEditForm(s)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-600" title="Edit" data-testid={`edit-sale-${s.id}`}><Pencil className="h-4 w-4" /></button>}
                          {!isReadOnly && isProvisional && <button onClick={() => askCancel(s)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-700" title="Cancel sale (free plot for reassignment)" data-testid={`cancel-sale-${s.id}`}><Ban className="h-4 w-4" /></button>}
                          {!isReadOnly && <button onClick={() => askDelete(s)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete permanently" data-testid={`delete-sale-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sales.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-neutral-500">No sales recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => !o && cancelDelete()}
        title="Delete this sale?"
        description={confirm.sale ? `This will permanently remove the sale for ${confirm.sale.client_info?.full_name || 'the client'}, all linked payments, and free its plot(s). This cannot be undone.` : ''}
        confirmLabel="Delete sale"
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={cancelConfirm.open}
        onOpenChange={(o) => !o && closeCancelConfirm()}
        title="Cancel this sale?"
        description={cancelConfirm.sale ? `The plot(s) currently assigned to ${cancelConfirm.sale.client_info?.full_name || 'this client'} will be freed for reassignment. Their payment records will be preserved so you can handle any refund externally. Sale status becomes "Cancelled".` : ''}
        confirmLabel="Cancel sale & free plot"
        destructive={false}
        onConfirm={confirmCancelSale}
      />
    </BOLayout>
  );
};

export default BOSales;
